import { Injectable, inject, signal, computed } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { environment } from 'src/environments/environment';

import { Ticket } from '../models/ticket.models';

@Injectable({
  providedIn: 'root',
})
export class CasesService {
  private http = inject(HttpClient);
  private apiUrl = `${environment.apiUrl}/v1/analytics/administrative/ticket`;

  private static readonly PAGE_SIZE = 20;
  private currentOrgId: number | null = null;
  private currentSkip = 0;

  // State
  public tickets = signal<Ticket[]>([]);
  public totalCount = signal<number>(0);
  public isLoading = signal<boolean>(false);
  public isLoadingMore = signal<boolean>(false);
  public error = signal<string | null>(null);
  public selectedTicketId = signal<string | null>(null);
  public activePriorityFilter = signal<string | null>(null);
  public activeStatusFilter = signal<string | null>(null);

  // Computed
  public filteredTickets = computed(() => this.tickets());
  public filteredCount = computed(() => this.tickets().length);
  public hasMore = computed(() => this.currentSkip < this.totalCount());

  public selectedTicket = computed(() => {
    const id = this.selectedTicketId();
    if (!id) return null;
    return this.tickets().find((t) => t.request_id === id) ?? null;
  });

  /**
   * Load first page of tickets. Resets pagination.
   */
  public loadTickets(organizationId: number | null): void {
    this.currentOrgId = organizationId;
    this.currentSkip = 0;
    this.isLoading.set(true);
    this.error.set(null);

    this.fetchTickets(0).subscribe({
      next: (response) => {
        const tickets = response.value ?? [];
        this.tickets.set(tickets);
        this.totalCount.set(response['@odata.count'] ?? tickets.length);
        this.currentSkip = tickets.length;
        this.isLoading.set(false);

        // Auto-select first ticket if none selected or selection no longer in list
        const currentSelection = this.selectedTicketId();
        const selectionStillValid = currentSelection && tickets.some((t) => t.request_id === currentSelection);
        if (!selectionStillValid && tickets.length > 0) {
          this.selectedTicketId.set(tickets[0].request_id);
        }
      },
      error: (err) => {
        console.error('Failed to load cases:', err);
        this.error.set('Failed to load cases. Please try again.');
        this.isLoading.set(false);
      },
    });
  }

  /**
   * Load next page (infinite scroll). No-op if already loading or no more data.
   */
  public loadNextPage(): void {
    if (this.isLoadingMore() || !this.hasMore()) return;

    this.isLoadingMore.set(true);

    this.fetchTickets(this.currentSkip).subscribe({
      next: (response) => {
        const newTickets = response.value ?? [];
        this.tickets.update((existing) => [...existing, ...newTickets]);
        this.totalCount.set(response['@odata.count'] ?? this.tickets().length);
        this.currentSkip = this.tickets().length;
        this.isLoadingMore.set(false);
      },
      error: (err) => {
        console.error('Failed to load more cases:', err);
        this.isLoadingMore.set(false);
      },
    });
  }

  /**
   * Select a ticket by request_id.
   */
  public selectTicket(requestId: string): void {
    this.selectedTicketId.set(requestId);
  }

  /**
   * Set priority filter and reload from API.
   */
  public setPriorityFilter(value: string | null): void {
    this.activePriorityFilter.set(value);
    this.loadTickets(this.currentOrgId);
  }

  /**
   * Set status filter and reload from API.
   */
  public setStatusFilter(value: string | null): void {
    this.activeStatusFilter.set(value);
    this.loadTickets(this.currentOrgId);
  }

  // ---------------------------------------------------------------------------
  // API
  // ---------------------------------------------------------------------------

  private fetchTickets(skip: number) {
    const url = this.currentOrgId
      ? `${this.apiUrl}(${this.currentOrgId})/`
      : `${this.apiUrl}/`;

    let params = new HttpParams()
      .set('$skip', skip.toString())
      .set('$top', CasesService.PAGE_SIZE.toString())
      .set('$orderby', 'created_time desc');

    const filterClauses = this.buildFilterClauses();
    if (filterClauses) {
      params = params.set('$filter', filterClauses);
    }

    return this.http.get<{ '@odata.count'?: number; value: Ticket[] }>(url, { params });
  }

  // ---------------------------------------------------------------------------
  // OData filter builder (matches mobile app's logic)
  // ---------------------------------------------------------------------------

  private buildFilterClauses(): string | null {
    const parts: string[] = [];

    const priority = this.activePriorityFilter();
    if (priority) {
      parts.push(`priority eq '${priority}'`);
    }

    const status = this.activeStatusFilter();
    if (status) {
      parts.push(this.statusODataClause(status));
    }

    return parts.length > 0 ? parts.join(' and ') : null;
  }

  private statusODataClause(status: string): string {
    switch (status) {
      case 'open':
        return 'ticket_closed_time eq null';
      case 'closed':
        return 'ticket_closed_time ne null';
      case 'awaiting_client':
        return "contains(ticket_status, 'Awaiting Response from Client')";
      default:
        return `ticket_status eq '${status}'`;
    }
  }
}
